import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faFileExport, faImages } from "@fortawesome/pro-regular-svg-icons";
import {
  faChevronDown,
  faChevronUp,
  faEllipsisVertical,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { memo, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShareLinkFile } from "../entities";
import { cssVar } from "../utils/cssVar";
import { formatFileSize } from "../utils/fileFormat";
import { FileIcon } from "../utils/fileIcon";
import { FileSort, FileSortKey } from "../utils/fileSort";
import { selectableProps } from "../utils/selectable";
import {
  ItemActivation,
  KEYBOARD_FOCUS_TINT,
  useDeepLinkBlink,
  useFileItemInteraction,
  useLongPress,
} from "./fileCard";
import { SelectionCheckbox, UNCHECKED_BORDER_COLOR } from "./selectionCheckbox";

/** Edge length (px) of the row's selection checkbox. */
const CHECKBOX_SIZE = 18;
/** Base flex gap (px) between row cells (`gap-3`). */
const ROW_GAP = 12;
/** Gap (px) between the checkbox and the type icon. */
const CHECKBOX_TO_ICON_GAP = 16;
/**
 * Fixed width (px) of the leading checkbox column (its margin box): the checkbox
 * plus the extra margin that brings its distance from the type icon up to
 * {@link CHECKBOX_TO_ICON_GAP}. The header and folder rows reserve the same width
 * so the type icon / "Type" label line up across every row.
 */
export const LEAD_COL_W = CHECKBOX_SIZE + (CHECKBOX_TO_ICON_GAP - ROW_GAP);

/** Gap (px) between the two expanded action icons (preview ↔ reference). */
const ACTIONS_GAP = 20;
/** Diameter (px) an action icon button occupies in the row layout. */
const ACTION_BTN = 28;
/** Diameter (px) of an action button's actual hit target (and hover splash);
 *  enlarged via negative margins so the row layout doesn't shift. */
const ACTION_BTN_HIT = 40;
/**
 * Fixed width (px) of the trailing actions column. It fits the expanded
 * two-icon form (preview + reference); the compact kebab right-aligns within it.
 * The header and folder rows reserve the same width so the size / modified
 * columns line up across every row.
 */
export const ACTIONS_COL_W = ACTION_BTN * 2 + ACTIONS_GAP;

/** Muted resting color for the trailing action icons. */
const ACTION_IDLE = "#8A8A94";
/** Width (px) reserved for the sort chevron so the title never shifts. */
const CHEVRON_SLOT_W = 12;

/** Placeholder shown (in the same style as a date) when no modified date exists. */
const NO_DATE = "--";

export function formatModified(
  modifiedAt: ShareLinkFile["modifiedAt"] | null,
  locale: string
): string {
  if (!modifiedAt) return NO_DATE;
  const date = modifiedAt instanceof Date ? modifiedAt : new Date(modifiedAt);
  if (Number.isNaN(date.getTime())) return NO_DATE;
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/**
 * Sort-direction chevron in a fixed-width slot that is *always* rendered (the
 * chevron itself only when the column is active), so clicking a header never
 * shifts its title. Ascending points UP, matching MUI's own `TableSortLabel`
 * and every file manager the user already knows (Finder, Explorer, Drive).
 */
function SortChevronSlot({
  active,
  direction,
}: {
  active: boolean;
  direction: "asc" | "desc";
}) {
  return (
    <span
      style={{
        width: CHEVRON_SLOT_W,
        display: "inline-flex",
        justifyContent: "center",
        flex: "none",
      }}
    >
      {active && (
        <FontAwesomeIcon
          icon={(direction === "asc" ? faChevronUp : faChevronDown) as IconProp}
          style={{ fontSize: 8 }}
        />
      )}
    </span>
  );
}

/** Header-title typography per `headerTitleSpec`: IBM Plex Sans Medium 12/22,
 *  #1A1A1A, sentence case (not uppercase). Shared by the sortable and static
 *  header cells so both read identically. */
const HEADER_TITLE_CLASS =
  "font-ibm-plex-sans text-[12px] font-medium leading-[22px] text-[#1A1A1A]";

/**
 * A single clickable column header. The whole cell is the hit-target and the
 * active column shows a direction chevron in an always-reserved slot (no shift
 * on click). `aria-sort` exposes the state to assistive tech.
 */
function SortableHeaderCell({
  columnKey,
  label,
  sort,
  onSort,
  className,
  align = "left",
}: {
  columnKey: FileSortKey;
  label: string;
  sort: FileSort | null;
  onSort: (key: FileSortKey) => void;
  className?: string;
  align?: "left" | "right";
}) {
  const active = sort?.key === columnKey;
  return (
    <div
      role="columnheader"
      aria-sort={
        active && sort
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
      className={className}
    >
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        className={`flex w-full items-center gap-1 whitespace-nowrap rounded-[4px] outline-none transition-opacity hover:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--primary-color)] ${HEADER_TITLE_CLASS} ${
          align === "right" ? "justify-end" : "justify-start"
        }`}
      >
        <span>{label}</span>
        <SortChevronSlot active={active} direction={sort?.direction ?? "asc"} />
      </button>
    </div>
  );
}

export type FileListHeaderProps = {
  /**
   * {@link onSort} makes every column a sort control; `sort` names the active
   * one, which then shows a direction chevron. Omit `onSort` for a static
   * header, and pass `sort` as `null` for sortable columns with nothing sorted
   * yet.
   */
  sort?: FileSort | null;
  onSort?: (key: FileSortKey) => void;
  /** Reserve a trailing spacer matching the rows' actions column. */
  hasActions?: boolean;
  /** Pin the header to the top of the scroll container as rows scroll under it. */
  sticky?: boolean;
};

/**
 * Column header for the list view. Mirrors {@link FileListRow}'s flex columns —
 * a leading checkbox-width spacer, type, name, then the size/modified group and
 * (in published space) a trailing actions column — so labels line up with the
 * cells beneath them and hide in lockstep via the `.file-list-col-*` classes.
 * The name column flexes to fill the free width, so the size/modified group and
 * the trailing actions column stay right-aligned at fixed widths.
 * Pass {@link FileListHeaderProps.sort} + `onSort` to make columns
 * clickable sort controls (published space); omit for a static header.
 */
export function FileListHeader({
  sort,
  onSort,
  hasActions,
  sticky,
}: FileListHeaderProps) {
  const { t } = useTranslation();
  return (
    <div
      role="row"
      className="flex items-center gap-3 px-3 pb-2"
      style={
        sticky
          ? {
              position: "sticky",
              top: 0,
              zIndex: 3,
              paddingTop: 8,
              backgroundColor: "#ffffff",
              borderBottom: "1px solid rgba(17,17,26,0.06)",
            }
          : undefined
      }
    >
      {/* Leading spacer aligns the labels with the rows' checkbox column. */}
      <div style={{ width: LEAD_COL_W }} className="shrink-0" aria-hidden />
      {/* The click handler alone decides whether the columns sort: an unsorted
          listing (`sort === null`) still needs its headers to be the way in. */}
      {onSort ? (
        <>
          <SortableHeaderCell
            columnKey="type"
            label={t("Type")}
            sort={sort ?? null}
            onSort={onSort}
            className="w-[33px] shrink-0"
          />
          <SortableHeaderCell
            columnKey="name"
            label={t("Name")}
            sort={sort ?? null}
            onSort={onSort}
            className="min-w-0 flex-1"
          />
          <div className="flex shrink-0 items-center gap-4">
            <SortableHeaderCell
              columnKey="size"
              label={t("Size")}
              sort={sort ?? null}
              onSort={onSort}
              className="file-list-col-size w-24"
            />
            <SortableHeaderCell
              columnKey="modified"
              label={t("Last modified")}
              sort={sort ?? null}
              onSort={onSort}
              className="file-list-col-modified w-28"
            />
          </div>
          {hasActions && (
            <div
              style={{ width: ACTIONS_COL_W }}
              className="shrink-0"
              aria-hidden
            />
          )}
        </>
      ) : (
        <>
          <div className={`w-[33px] shrink-0 ${HEADER_TITLE_CLASS}`}>
            {t("Type")}
          </div>
          <div className={`min-w-0 flex-1 ${HEADER_TITLE_CLASS}`}>
            {t("Name")}
          </div>
          <div className="flex shrink-0 items-center gap-4">
            <div className={`file-list-col-size w-24 ${HEADER_TITLE_CLASS}`}>
              {t("Size")}
            </div>
            <div
              className={`file-list-col-modified w-28 ${HEADER_TITLE_CLASS}`}
            >
              {t("Last modified")}
            </div>
          </div>
          {hasActions && (
            <div
              style={{ width: ACTIONS_COL_W }}
              className="shrink-0"
              aria-hidden
            />
          )}
        </>
      )}
    </div>
  );
}

export type FileListRowProps = {
  file: ShareLinkFile;
  isSelected: boolean;
  onToggleSelect: (uid: string) => void;
  /** Invoked with the file when the row is activated to open its preview. */
  onOpenPreview: (file: ShareLinkFile) => void;
  /** True when ≥1 file is selected anywhere (selection mode is active). */
  selectionActive: boolean;
  /** True while a marquee drag is in progress (suppresses hover + pointer cursor). */
  selecting?: boolean;
  /**
   * Click activation model (see {@link ItemActivation}). `"select"` also skips
   * the row's checkbox in the tab order (the row itself is the tab stop).
   * Defaults to `"preview"`.
   */
  activation?: ItemActivation;
  /** Plain-click exclusive select for `"select"` activation (see {@link useFileItemInteraction}). */
  onSelectExclusive?: (uid: string) => void;
  /** Shift+click range select for `"select"` activation (see {@link useFileItemInteraction}). */
  onSelectRange?: (uid: string) => void;
  /** Controlled highlight (same tint as keyboard focus) — for a deep-linked row. */
  focused?: boolean;
  /**
   * Keep the checkbox visible instead of revealing it on hover / focus /
   * selection. Both viewers pass the coarse-pointer flag, so touch (which has no
   * hover to surface it) shows it the whole time. Space is reserved either way,
   * so revealing it never shifts the row.
   */
  checkboxAlwaysVisible?: boolean;
  /**
   * "Reference this file" action. When provided together with
   * {@link onMoreActions}, the row grows a trailing actions column.
   */
  onReference?: (file: ShareLinkFile) => void;
  /**
   * Opens the overflow (kebab) menu, anchored to the button. Rendered as the
   * compact form of the actions column (and required to show any actions).
   */
  onMoreActions?: (anchorEl: HTMLElement, file: ShareLinkFile) => void;
  /**
   * Collapse the trailing actions to a single kebab (⋮) instead of the two
   * inline icon buttons. Set when the viewport is too narrow to fit both.
   */
  compactActions?: boolean;
  /** True while this row's overflow menu is open: keeps the actions visible. */
  menuOpen?: boolean;
  /** Adjacent row is also selected: the shared corners flatten so a
   *  consecutive selection reads as one block. */
  selectedAbove?: boolean;
  selectedBelow?: boolean;
};

/**
 * List-view row for a single shared file. Columns: a leading selection checkbox,
 * type icon, name (flexes into the free width, truncating with a full-name
 * tooltip), size,
 * last-modified and — in published space — a trailing actions column (preview +
 * reference, collapsing to a kebab when {@link FileListRowProps.compactActions}),
 * revealed on hover/focus like the checkbox (always on touch).
 * As the viewport narrows, last-modified hides first, then size (see the
 * `.file-list-col-*` media queries in `index.css`); the checkbox, name and
 * actions always remain.
 *
 * Touch selection: a long-press selects the row (entering selection mode); once
 * any file is selected (`selectionActive`), a plain tap toggles selection
 * instead of opening the preview. A click immediately following a long-press is
 * swallowed. Shares the rest of its behavior with the grid card via
 * {@link useFileItemInteraction}.
 */
export const FileListRow = memo(function FileListRow({
  file,
  isSelected,
  onToggleSelect,
  onOpenPreview,
  selectionActive,
  selecting = false,
  activation = "preview",
  onSelectExclusive,
  onSelectRange,
  focused = false,
  checkboxAlwaysVisible = false,
  onReference,
  onMoreActions,
  compactActions = false,
  menuOpen = false,
  selectedAbove = false,
  selectedBelow = false,
}: FileListRowProps) {
  const { i18n } = useTranslation();
  const { displayName, handleClick, handleDoubleClick, handleKeyDown } =
    useFileItemInteraction(
      file,
      onToggleSelect,
      onOpenPreview,
      activation,
      onSelectExclusive,
      onSelectRange
    );

  // Pointer hover (suppressed during a marquee drag) drives the checkbox reveal
  // and the action-icon color shift.
  const [isHovered, setIsHovered] = useState(false);
  // Measured on each hover; a fully visible name shows no tooltip.
  const [isNameTruncated, setIsNameTruncated] = useState(false);
  // Deep-link blink for a referenced (focused) row.
  const blink = useDeepLinkBlink(focused);
  // Long-press selects on touch; the click synthesized after it is swallowed.
  const { handlers: pressHandlers, consumeClick } = useLongPress(() =>
    onToggleSelect(file.uid)
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      if (consumeClick()) return;
      // In "preview" mode a tap toggles once selection is active; in "select"
      // mode `handleClick` owns the gesture (single click selects, double
      // click opens the preview).
      if (activation === "preview" && selectionActive) {
        onToggleSelect(file.uid);
        return;
      }
      handleClick(e);
    },
    [
      consumeClick,
      activation,
      selectionActive,
      onToggleSelect,
      file.uid,
      handleClick,
    ]
  );

  const showHover = isHovered && !selecting;
  const showActions = !!(onReference && onMoreActions);
  // Reveal on hover, deep-link highlight or open menu; always on touch.
  // Keyboard focus reveals via CSS (index.css). Width is always reserved.
  const showActionButtons =
    showActions && (checkboxAlwaysVisible || showHover || focused || menuOpen);
  // Checkbox: always-on (share-link / touch), or revealed by mouse hover,
  // selection, the deep-link highlight or an open menu — the same triggers as
  // the actions, so a row never shows one without the other. Deliberately NOT
  // revealed by keyboard focus, so tabbing through rows doesn't flash every
  // checkbox. Space is reserved regardless, so revealing it never shifts the row.
  const showCheckbox =
    checkboxAlwaysVisible || isSelected || showHover || focused || menuOpen;

  return (
    <div
      {...selectableProps(file.uid)}
      role="button"
      tabIndex={0}
      aria-label={displayName}
      aria-pressed={isSelected}
      className={`file-card flex select-none items-center gap-3 px-3 outline-none${
        selecting ? "" : " file-list-row"
      }${blink && !isSelected ? " file-deeplink-blink" : ""}`}
      style={{
        height: 52,
        cursor: selecting ? "default" : "pointer",
        // Selected fill, deeper under the cursor (so the hovered row stands
        // out in a multi-selection); undefined otherwise so CSS rules apply.
        backgroundColor: isSelected
          ? showHover
            ? "color-mix(in srgb, var(--primary-color) 14%, var(--primary-light-color))"
            : cssVar("--primary-light-color")
          : focused
          ? KEYBOARD_FOCUS_TINT
          : undefined,
        // Shared corners flatten between adjacent selected rows.
        borderRadius:
          isSelected && selectedAbove
            ? isSelected && selectedBelow
              ? "0"
              : "0 0 14px 14px"
            : isSelected && selectedBelow
            ? "14px 14px 0 0"
            : "14px",
        transition: "background-color 0.12s ease, border-radius 0.12s ease",
      }}
      onClick={handleRowClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => {
        if (!selecting) setIsHovered(true);
      }}
      onMouseLeave={() => setIsHovered(false)}
      {...pressHandlers}
    >
      {/* Checkbox column; revealed with the same fade as the actions, space
          always reserved. */}
      <span
        className="flex shrink-0"
        style={{
          marginRight: CHECKBOX_TO_ICON_GAP - ROW_GAP,
          opacity: showCheckbox ? 1 : 0,
          visibility: showCheckbox ? "visible" : "hidden",
          transition: "opacity 0.16s ease, visibility 0.16s",
        }}
      >
        <SelectionCheckbox
          checked={isSelected}
          onChange={() => onToggleSelect(file.uid)}
          itemName={displayName}
          size={CHECKBOX_SIZE}
          tabIndex={activation === "select" ? -1 : undefined}
        />
      </span>
      <div className="w-[33px] shrink-0">
        <FileIcon mimeType={file.mimeType} fileName={displayName} flat />
      </div>
      {/* Anchored to a span hugging the visible text; only shown when the name
          is actually truncated (empty title disables it). */}
      <div className="min-w-0 flex-1">
        <Tooltip
          title={isNameTruncated ? displayName : ""}
          enterDelay={400}
          placement="top"
        >
          <span
            className="inline-block max-w-full truncate align-top text-[15px] font-normal leading-[22px]"
            onMouseEnter={(e) =>
              setIsNameTruncated(
                e.currentTarget.scrollWidth > e.currentTarget.clientWidth + 1
              )
            }
          >
            {displayName}
          </span>
        </Tooltip>
      </div>
      {/* Size + last-modified per `fileSizeSpec`: IBM Plex Sans Regular 15/22,
          #1A1A1A, left-aligned (matching the Name column). */}
      <div className="flex shrink-0 items-center gap-4 text-[15px] leading-[22px] text-[#1A1A1A]">
        <div className="file-list-col-size w-24 text-left">
          {formatFileSize(file.size, i18n.language)}
        </div>
        <div className="file-list-col-modified w-28 text-left">
          {formatModified(file.modifiedAt, i18n.language)}
        </div>
      </div>
      {showActions && (
        // `visibility` removes hidden buttons from hit-testing and tabbing.
        <div
          className="file-list-row-actions shrink-0"
          style={{
            opacity: showActionButtons ? 1 : 0,
            visibility: showActionButtons ? "visible" : "hidden",
            transition: "opacity 0.16s ease, visibility 0.16s",
          }}
        >
          <RowActions
            file={file}
            compact={compactActions}
            rowHovered={showHover}
            onPreview={onOpenPreview}
            onReference={onReference!}
            onMoreActions={onMoreActions!}
          />
        </div>
      )}
    </div>
  );
});

// ==========================================================================
//                             Trailing row actions
// ==========================================================================

const ACTION_BTN_SX = {
  width: ACTION_BTN_HIT,
  height: ACTION_BTN_HIT,
  margin: `${(ACTION_BTN - ACTION_BTN_HIT) / 2}px`,
  flex: "none",
  "&:hover": { backgroundColor: "rgba(17,17,26,0.06)" },
} as const;

/** A ripple-free icon button used inside {@link RowActions}. Its color shifts
 *  to the primary color when the row is hovered (`active`), with an optional
 *  per-icon `delay` for a cascade. Stops its own pointer events so acting never
 *  activates the row or starts a marquee. */
export function RowActionButton({
  icon,
  label,
  iconSize = 15,
  active,
  delay = 0,
  idleColor = ACTION_IDLE,
  onClick,
}: {
  icon: IconProp;
  label: string;
  iconSize?: number;
  active: boolean;
  delay?: number;
  idleColor?: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <Tooltip title={label} placement="top">
      <IconButton
        size="small"
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        // Stop the pointer event too, so pressing an action doesn't fire the
        // row's press (scale) feedback.
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        sx={{
          ...ACTION_BTN_SX,
          color: active ? "var(--primary-color)" : idleColor,
          transition: `color 0.28s ease ${delay}s, background-color 0.15s ease`,
          "&.Mui-focusVisible": {
            outline: "2px solid var(--primary-color)",
            outlineOffset: "1px",
          },
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ fontSize: iconSize }} />
      </IconButton>
    </Tooltip>
  );
}

/** One cross-faded slot. `visible` drives opacity/scale; `enterDelay`/
 *  `leaveDelay` (seconds) stagger the hand-off so the expanded and compact
 *  forms trade places in a cascade rather than dissolving on top of each other.
 *  `spring` gives the compact kebab a slight overshoot as it pops in. The whole
 *  layer's pointer events follow `visible` so the hidden form never intercepts a
 *  hover meant for the visible one (which had swallowed the reference tooltip). */
function ActionSlot({
  visible,
  enterDelay = 0,
  leaveDelay = 0,
  spring = false,
  children,
}: {
  visible: boolean;
  enterDelay?: number;
  leaveDelay?: number;
  spring?: boolean;
  children: ReactNode;
}) {
  const delay = visible ? enterDelay : leaveDelay;
  return (
    <span
      aria-hidden={!visible}
      style={{
        display: "inline-flex",
        opacity: visible ? 1 : 0,
        transform: visible ? "scale(1)" : spring ? "scale(0.55)" : "scale(0.8)",
        pointerEvents: visible ? "auto" : "none",
        transitionProperty: "opacity, transform",
        transitionDuration: "0.2s, 0.3s",
        transitionTimingFunction: spring
          ? "ease, cubic-bezier(0.34, 1.56, 0.64, 1)"
          : "ease, cubic-bezier(0.22, 1, 0.36, 1)",
        transitionDelay: `${delay}s, ${delay}s`,
      }}
    >
      {children}
    </span>
  );
}

/**
 * Trailing actions for a file row. Two forms occupy the same fixed-width column
 * ({@link ACTIONS_COL_W}) and cross-fade based on `compact`:
 *  - expanded: inline Preview + Reference icon buttons (20px apart),
 *  - compact: a single kebab (⋮) opening the same actions in a menu.
 * When space runs out (`compact`) the two icons cascade away (reference first)
 * and the kebab springs in just after, and vice-versa on the way back. When the
 * row is hovered the preview/reference icons light up to the primary color in a
 * short cascade; the kebab keeps a constant color.
 */
function RowActions({
  file,
  compact,
  rowHovered,
  onPreview,
  onReference,
  onMoreActions,
}: {
  file: ShareLinkFile;
  compact: boolean;
  rowHovered: boolean;
  onPreview: (file: ShareLinkFile) => void;
  onReference: (file: ShareLinkFile) => void;
  onMoreActions: (anchorEl: HTMLElement, file: ShareLinkFile) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="relative shrink-0"
      style={{ width: ACTIONS_COL_W, height: ACTION_BTN }}
    >
      {/* Expanded: preview + reference, right-aligned, 20px apart. */}
      <div
        className="absolute inset-y-0 right-0 flex items-center"
        style={{ gap: ACTIONS_GAP, pointerEvents: compact ? "none" : "auto" }}
      >
        <ActionSlot visible={!compact} enterDelay={0.02} leaveDelay={0.09}>
          <RowActionButton
            icon={faImages as IconProp}
            label={t("Preview")}
            iconSize={17}
            active={rowHovered}
            delay={0}
            onClick={() => onPreview(file)}
          />
        </ActionSlot>
        <ActionSlot visible={!compact} enterDelay={0.1} leaveDelay={0}>
          <RowActionButton
            icon={faFileExport as IconProp}
            label={t("Reference this file")}
            iconSize={17}
            active={rowHovered}
            delay={0.06}
            onClick={() => onReference(file)}
          />
        </ActionSlot>
      </div>
      {/* Compact: a single kebab, right-aligned in the same column. */}
      <div
        className="absolute inset-y-0 right-0 flex items-center"
        style={{ pointerEvents: compact ? "auto" : "none" }}
      >
        <ActionSlot visible={compact} enterDelay={0.11} leaveDelay={0} spring>
          <RowActionButton
            icon={faEllipsisVertical as IconProp}
            label={t("More actions")}
            iconSize={16}
            // Constant checkbox-matching color; no hover/menu highlight.
            active={false}
            idleColor={UNCHECKED_BORDER_COLOR}
            onClick={(e) => onMoreActions(e.currentTarget, file)}
          />
        </ActionSlot>
      </div>
    </div>
  );
}
